/**
 * CV store with quota enforcement for local mode
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { QuotaManager } from '@/services/quota/QuotaManager';
import { QuotaExceededError } from '@/types/errors';
import { useAuthStore } from './auth.store';
import type { CV, CVGenerationOptions } from '@shared/types/cv';
import { trackCvGenerated } from '@/services/analytics';
import { cvService } from '@/services/cv.service';

interface CVState {
  generatedCVs: CV[];
  currentCV: CV | null;
  isGenerating: boolean;
  hasFetchedFromBackend: boolean;
}

interface CVActions {
  generateCV: (options: CVGenerationOptions & { profileId: string; templateId: string; vacancyId?: string }) => Promise<CV>;
  setCurrentCV: (cv: CV | null) => void;
  addGeneratedCV: (cv: CV) => void;
  removeCV: (id: string) => void;
  clearCVs: () => void;
  fetchFromBackend: () => Promise<void>;
}

export type CVStore = CVState & CVActions;

export const useCVStore = create<CVStore>()(
  persist(
    (set) => ({
      // State
      generatedCVs: [],
      currentCV: null,
      isGenerating: false,
      hasFetchedFromBackend: false,

      // Actions

      /**
       * Fetch CVs from backend (cloud mode only).
       * Replaces local state with backend data.
       */
      fetchFromBackend: async () => {
        const { isAuthenticated } = useAuthStore.getState();
        if (!isAuthenticated) return;

        try {
          const backendCVs = await cvService.listCVs();
          // Map backend GeneratedCV to CV type (guard against non-array)
          const cvList = Array.isArray(backendCVs) ? backendCVs : [];
          const cvs: CV[] = cvList.map((cv) => ({
            id: cv.id,
            userId: cv.userId,
            profileId: cv.profileId || '',
            vacancyId: cv.vacancyId || null,
            templateId: cv.templateId,
            content: cv.content || {},
            rendered: cv.rendered || '',
            metadata: {},
            createdAt: new Date(cv.createdAt),
            updatedAt: new Date(cv.createdAt),
          }));
          set({ generatedCVs: cvs, hasFetchedFromBackend: true });
        } catch (error) {
          console.error('Failed to fetch CVs from backend:', error);
        }
      },

      /**
       * Generate CV with quota check.
       * Throws QuotaExceededError if limit reached in local mode.
       */
      generateCV: async (options) => {
        const { mode } = useAuthStore.getState();

        // Quota check for local mode
        if (mode === 'local' && !QuotaManager.canGenerateCV()) {
          throw new QuotaExceededError('cv_generation', QuotaManager.getRemainingCVs());
        }

        set({ isGenerating: true });

        try {
          // TODO: Call backend API (works in both local and cloud mode)
          // For now, create a mock CV
          const result: CV = {
            id: `cv-${Date.now()}`,
            userId: mode === 'cloud' ? 'user-id' : 'local',
            profileId: options.profileId,
            vacancyId: options.vacancyId || null,
            templateId: options.templateId,
            content: {},
            rendered: '',
            metadata: {
              language: options.language,
              length: options.length,
              format: options.format,
            },
            createdAt: new Date(),
            updatedAt: new Date(),
          };

          // Increment quota AFTER successful generation
          if (mode === 'local') {
            QuotaManager.incrementCVGeneration(result.id);
          }

          // Track analytics — fire-and-forget
          trackCvGenerated(result.templateId);

          // Add to store
          set((s) => ({
            generatedCVs: [...s.generatedCVs, result],
            currentCV: result,
            isGenerating: false,
          }));

          return result;
        } catch (error) {
          set({ isGenerating: false });
          throw error;
        }
      },

      setCurrentCV: (cv) => {
        set({ currentCV: cv });
      },

      addGeneratedCV: (cv) => {
        set((s) => ({
          generatedCVs: [...s.generatedCVs, cv],
        }));
      },

      removeCV: (id) => {
        set((s) => ({
          generatedCVs: s.generatedCVs.filter((cv) => cv.id !== id),
          currentCV: s.currentCV?.id === id ? null : s.currentCV,
        }));
      },

      clearCVs: () => {
        set({
          generatedCVs: [],
          currentCV: null,
        });
      },
    }),
    {
      name: 'cv-storage',
      partialize: (state) => ({
        generatedCVs: state.generatedCVs,
        currentCV: state.currentCV,
      }),
    }
  )
);
